function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { useEffect, useMemo, useState } from 'react';
import { MembersAPI } from 'data/members/api';
const LRU_CAPACITY = 200;
class LRUCache {
    get(key) {
        const value = this.cache.get(key);
        if (value === undefined) {
            return undefined;
        }
        // Move to end (most recently used)
        this.cache.delete(key);
        this.cache.set(key, value);
        return value;
    }
    set(key, value) {
        // If key exists, remove it first (will be re-added at end)
        if (this.cache.has(key)) {
            this.cache.delete(key);
        } else if (this.cache.size >= this.capacity) {
            // Evict least recently used (first entry)
            const firstKey = this.cache.keys().next().value;
            if (firstKey !== undefined) {
                this.cache.delete(firstKey);
            }
        }
        this.cache.set(key, value);
    }
    has(key) {
        return this.cache.has(key);
    }
    delete(key) {
        return this.cache.delete(key);
    }
    clear() {
        this.cache.clear();
    }
    constructor(capacity){
        _define_property(this, "cache", new Map());
        _define_property(this, "capacity", void 0);
        this.capacity = capacity;
    }
}
const userCache = new LRUCache(LRU_CAPACITY);
/**
 * Invalidate a specific user from the SWR cache.
 * Call this when a user's data is mutated (profile update, avatar change, etc.)
 */ export const invalidateSwrUser = (userId)=>{
    userCache.delete(userId);
};
/**
 * Clear the entire SWR user cache.
 * Prefer invalidateSwrUser(userId) for targeted invalidation.
 */ export const clearSwrCache = ()=>{
    userCache.clear();
};
let pendingRequests = [];
let batchTimer = null;
const BATCH_DELAY = 100; // milliseconds to wait before batching requests
const fetchBatchedUsers = async ()=>{
    if (pendingRequests.length === 0) {
        return;
    }
    // Get all pending requests and clear the queue
    const requestsToProcess = [
        ...pendingRequests
    ];
    pendingRequests = [];
    // Extract unique userIds that are not already in cache
    const userIdsToFetch = Array.from(new Set(requestsToProcess.map((req)=>req.userId).filter((id)=>!userCache.has(id))));
    // If all users are already cached, resolve immediately
    if (userIdsToFetch.length === 0) {
        requestsToProcess.forEach((req)=>{
            var _userCache_get;
            req.resolve((_userCache_get = userCache.get(req.userId)) !== null && _userCache_get !== void 0 ? _userCache_get : null);
        });
        return;
    }
    try {
        var _ref, _ref1;
        // Make a single batched API call
        const response = await MembersAPI.fetchPaginatedMembersData({
            includedUserIds: userIdsToFetch
        });
        // Update cache with fetched users from both possible arrays
        // When using includedUserIds, API returns users in includedUsers array
        const fetchedUsers = [
            ...(_ref = response === null || response === void 0 ? void 0 : response.includedUsers) !== null && _ref !== void 0 ? _ref : [],
            ...(_ref1 = response === null || response === void 0 ? void 0 : response.notInProjectUsers) !== null && _ref1 !== void 0 ? _ref1 : []
        ];
        if (fetchedUsers.length) {
            fetchedUsers.forEach((user)=>{
                userCache.set(user.userId, user);
            });
        }
        // Resolve all pending requests
        requestsToProcess.forEach((req)=>{
            var _userCache_get;
            req.resolve((_userCache_get = userCache.get(req.userId)) !== null && _userCache_get !== void 0 ? _userCache_get : null);
        });
    } catch (error) {
        // Reject all pending requests on error
        requestsToProcess.forEach((req)=>{
            req.reject(error);
        });
    }
};
const scheduleBatchFetch = ()=>{
    if (batchTimer) {
        clearTimeout(batchTimer);
    }
    batchTimer = setTimeout(()=>{
        batchTimer = null;
        fetchBatchedUsers();
    }, BATCH_DELAY);
};
export const requestUser = (userId)=>{
    // If already in cache, return immediately
    const cached = userCache.get(userId);
    if (cached) {
        return Promise.resolve(cached);
    }
    // Add to pending requests
    return new Promise((resolve, reject)=>{
        pendingRequests.push({
            userId,
            resolve,
            reject
        });
        scheduleBatchFetch();
    });
};
export const getUserFromSwrCache = (userId)=>userCache.get(userId);
export const useUserSwr = (userId)=>{
    var _userCache_get;
    const [user, setUser] = useState(userId != null ? (_userCache_get = userCache.get(userId)) !== null && _userCache_get !== void 0 ? _userCache_get : null : null);
    useEffect(()=>{
        if (!userId) {
            setUser(null);
            return undefined;
        }
        let cancelled = false;
        // If already in cache, don't fetch
        const cached = userCache.get(userId);
        if (cached) {
            setUser(cached);
            return ()=>{
                cancelled = true;
            };
        }
        // Clear stale data from a previous userId while fetching the new one
        setUser(null);
        // Request user (will be batched with other requests)
        requestUser(userId).then((fetchedUser)=>{
            if (!cancelled) {
                setUser(fetchedUser);
            }
        }).catch(()=>{
            if (!cancelled) {
                setUser(null);
            }
        });
        return ()=>{
            cancelled = true;
        };
    }, [
        userId
    ]);
    // Memoize the user value to provide stable identity when unchanged
    return useMemo(()=>user, [
        user
    ]);
};
/**
 * Batch-fetch multiple users by IDs using the SWR cache.
 * Returns a Record<userId, User> map for easy lookup.
 * Uses the same batching/caching mechanism as useUserSwr.
 */ export const requestUsers = async (userIds)=>{
    const uniqueIds = [
        ...new Set(userIds.filter((id)=>id != null))
    ];
    if (uniqueIds.length === 0) {
        return {};
    }
    const users = await Promise.all(uniqueIds.map((id)=>requestUser(id)));
    const map = {};
    users.forEach((user)=>{
        if (user) {
            map[user.userId] = user;
        }
    });
    return map;
};
export const useUsersSwr = (userIds)=>{
    const [users, setUsers] = useState(()=>userIds.map((id)=>userCache.get(id)).filter(Boolean));
    const userIdsKey = useMemo(()=>userIds.join(','), [
        userIds
    ]);
    useEffect(()=>{
        if (userIds.length === 0) {
            setUsers([]);
            return undefined;
        }
        let cancelled = false;
        Promise.all(userIds.map((id)=>requestUser(id))).then((results)=>{
            if (!cancelled) {
                setUsers(results.filter(Boolean));
            }
        }).catch(()=>{
            if (!cancelled) {
                setUsers([]);
            }
        });
        return ()=>{
            cancelled = true;
        };
    }, [
        userIdsKey
    ]);
    return useMemo(()=>users, [
        users
    ]);
};
